import React from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import KanbanColumn from "./KanbanColumn";
import TaskCard from "./TaskCard";
import { computeColumnReorder } from "./columnUtils";
import { rootTasksForColumn } from "./taskFilterUtils";

export function findColumnId(rootTasks, over) {
  if (!over) return null;
  const typed = over.data?.current;
  if (typed?.type === "column" && typed.column?.id != null) {
    return typed.column.id;
  }
  if (typed?.type === "task" && typed.task?.column_id != null) {
    return typed.task.column_id;
  }
  const overId = over.id;
  if (!overId) return null;
  const overStr = String(overId);
  if (overStr.startsWith("column-drop-")) {
    return Number(overStr.replace("column-drop-", ""));
  }
  if (overStr.startsWith("column-")) {
    return Number(overStr.replace("column-", ""));
  }
  if (overStr.startsWith("task-")) {
    const taskId = Number(overStr.replace("task-", ""));
    const task = rootTasks.find((t) => t.id === taskId);
    return task?.column_id ?? null;
  }
  return null;
}

export function computeMovePosition(rootTasks, activeTask, targetColumnId, overId) {
  const fullColumnTasks = rootTasksForColumn(rootTasks, targetColumnId);
  let newPosition = fullColumnTasks.length;

  const overStr = String(overId);
  if (overStr.startsWith("task-")) {
    const overTaskId = Number(overStr.replace("task-", ""));
    const overIndex = fullColumnTasks.findIndex((t) => t.id === overTaskId);
    if (overIndex >= 0) {
      newPosition = overIndex;
    }
  }

  if (activeTask.column_id === targetColumnId) {
    const oldIndex = fullColumnTasks.findIndex((t) => t.id === activeTask.id);
    if (oldIndex === newPosition || oldIndex === newPosition - 1) {
      return null;
    }
    if (oldIndex < newPosition) {
      newPosition -= 1;
    }
  }

  return newPosition;
}

export function pickTaskCollisions(pointerCollisions, fallbackCollisions) {
  const pool = pointerCollisions?.length ? pointerCollisions : fallbackCollisions || [];
  if (!pool.length) return [];
  const byPrefix = (prefix) => pool.find((c) => String(c.id).startsWith(prefix));
  const chosen =
    byPrefix("task-") || byPrefix("column-drop-") || byPrefix("column-") || pool[0];
  return [chosen];
}

function collisionDetection(args) {
  if (args.active.data.current?.type === "column") {
    return pointerWithin(args);
  }
  return pickTaskCollisions(pointerWithin(args), closestCorners(args));
}

function KanbanColumns({
  columns,
  rootTasks,
  onTaskClick,
  dragDisabled,
  matchingTaskIds,
  onRenameColumn,
  onDeleteColumn,
  onMoveColumn,
  onToggleCollapsed,
  collapsedIds,
  onAddColumn,
  addingColumn,
  newColumnName,
  onNewColumnNameChange,
  onCancelAddColumn,
  onConfirmAddColumn,
  subtasksByParent,
  onToggleSubtask,
}) {
  const columnIds = columns.map((col) => `column-${col.id}`);

  return (
    <div className="kanban-board">
      <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
        {columns.map((col, index) => (
          <KanbanColumn
            key={col.id}
            column={col}
            columns={columns}
            tasks={col.tasks}
            totalTaskCount={col.totalTaskCount}
            onTaskClick={onTaskClick}
            dragDisabled={dragDisabled}
            matchingTaskIds={matchingTaskIds}
            onRenameColumn={onRenameColumn}
            onDeleteColumn={onDeleteColumn}
            onMoveColumn={onMoveColumn}
            onToggleCollapsed={onToggleCollapsed}
            collapsed={collapsedIds?.has(col.id)}
            columnIndex={index}
            columnCount={columns.length}
            subtasksByParent={subtasksByParent}
            onToggleSubtask={onToggleSubtask}
          />
        ))}
      </SortableContext>
      <div className="kanban-column kanban-column-add">
        {addingColumn ? (
          <form
            className="kanban-column-add-form"
            onSubmit={(e) => {
              e.preventDefault();
              onConfirmAddColumn?.();
            }}
          >
            <input
              className="task-form-input"
              name="new_column_name"
              autoComplete="off"
              placeholder="Например, Ревью…"
              value={newColumnName || ""}
              onChange={(e) => onNewColumnNameChange?.(e.target.value)}
              maxLength={100}
              autoFocus
              aria-label="Название новой колонки"
            />
            <div className="kanban-column-add-actions">
              <button type="submit" className="btn btn-primary btn-sm" disabled={!newColumnName?.trim()}>
                Добавить
              </button>
              <button type="button" className="btn btn-sm" onClick={onCancelAddColumn}>
                Отмена
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            className="kanban-column-add-btn"
            onClick={onAddColumn}
          >
            + Колонка
          </button>
        )}
      </div>
    </div>
  );
}

function KanbanBoardContent(props) {
  return (
    <>
      <p className="kanban-mobile-hint">Свайпните влево или вправо, чтобы переключить колонки</p>
      <KanbanColumns {...props} />
    </>
  );
}

export default function KanbanBoard({
  columns,
  rootTasks,
  onTaskClick,
  onMoveTask,
  onMoveColumn,
  onToggleCollapsed,
  collapsedIds,
  dragDisabled = false,
  matchingTaskIds,
  onRenameColumn,
  onDeleteColumn,
  onAddColumn,
  addingColumn,
  newColumnName,
  onNewColumnNameChange,
  onCancelAddColumn,
  onConfirmAddColumn,
  subtasksByParent,
  onToggleSubtask,
}) {
  const [activeTask, setActiveTask] = React.useState(null);
  const [activeColumn, setActiveColumn] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const contentProps = {
    columns,
    rootTasks,
    onTaskClick,
    dragDisabled,
    matchingTaskIds,
    onRenameColumn,
    onDeleteColumn,
    onMoveColumn,
    onToggleCollapsed,
    collapsedIds,
    onAddColumn,
    addingColumn,
    newColumnName,
    onNewColumnNameChange,
    onCancelAddColumn,
    onConfirmAddColumn,
    subtasksByParent,
    onToggleSubtask,
  };

  const handleDragStart = (event) => {
    const type = event.active.data.current?.type;
    if (type === "column") {
      setActiveColumn(event.active.data.current.column);
      return;
    }
    if (dragDisabled) return;
    const task = event.active.data.current?.task;
    if (task) setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const type = active.data.current?.type;
    setActiveTask(null);
    setActiveColumn(null);
    if (!over) return;

    if (type === "column") {
      const activeColumnData = active.data.current?.column;
      const targetColumnId = findColumnId(rootTasks, over);
      if (!activeColumnData || !targetColumnId) return;
      const next = computeColumnReorder(columns, activeColumnData.id, targetColumnId);
      if (!next) return;
      await onMoveColumn?.(next.columnId, next.position);
      return;
    }

    if (dragDisabled) return;
    const activeTaskData = active.data.current?.task;
    if (!activeTaskData) return;

    const targetColumnId = findColumnId(rootTasks, over);
    if (!targetColumnId) return;

    const newPosition = computeMovePosition(
      rootTasks,
      activeTaskData,
      targetColumnId,
      over.id
    );
    if (newPosition === null) return;

    await onMoveTask(activeTaskData.id, targetColumnId, newPosition);
  };

  const handleDragCancel = () => {
    setActiveTask(null);
    setActiveColumn(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <KanbanBoardContent {...contentProps} />
      <DragOverlay>
        {activeTask ? <TaskCard task={activeTask} isDragging /> : null}
        {activeColumn ? (
          <div className="kanban-column-overlay">
            <span className="kanban-status-dot kanban-dot-custom" aria-hidden="true" />
            {activeColumn.name}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
